import type {UseQueryOptions} from '@tanstack/react-query';
import {queryOptions} from '@tanstack/react-query';
import {requestContext} from '../../common/requestContext';
import {ViewerEndVo} from '../../common/shared/api/gateway/viewer/ViewerEndVo.interface';
import type {ViewerInfoVo} from '../../common/shared/api/gateway/viewer/ViewerInfoVo.interface';
import type {ApiResultVo} from '../../common/shared/api/nicolas/vo/ApiResultVo.interface';
import {PagewebError} from '../../errors/PagewebError.class';
import {createViewerEnd} from '../../graphql/factory/ViewerEnd';
import {ViewerEnd, ViewerInfo} from '../../graphql/generated/types';
import {VIEWER_QUERY_KEY_MAP} from './constant';
import {getViewerEnd} from './getViewerEnd';
import {getViewerInfo} from './getViewerInfo';

export type ViewerInfoResponse = ApiResultVo & {result: ViewerInfoVo};
export type ViewerEndResponse = ApiResultVo & {result: ViewerEndVo};

/**
 * Viewer 도메인 Query Factory
 */
export const viewerQueryFactory = {
  /**
   * 뷰어 정보 조회
   *
   * Origin: fetchViewerInfo
   * API: GET /api/v1/viewer/data
   *
   * @example
   * const {data} = useQuery(viewerQueryFactory.viewerInfo({seriesId: 123, productId: 456}));
   */
  viewerInfo: (
    variables: {seriesId: number; productId: number},
    options?: Omit<
      UseQueryOptions<ViewerInfo, PagewebError>,
      'queryKey' | 'queryFn'
    >
  ) =>
    queryOptions({
      queryKey: VIEWER_QUERY_KEY_MAP.viewerInfo(variables),
      queryFn: async () => {
        const response = await getViewerInfo(variables);
        if (!response.viewerData) {
          throw new PagewebError({
            message: 'Viewer info not found',
            resultCode: response.result_code,
          });
        }
        return response;
      },
      ...options,
    }),

  /**
   * 뷰어 엔드 조회
   *
   * Origin: fetchViewerEnd
   * API: GET /api/v2/viewer/end
   *
   * @example
   * const {data} = useQuery(viewerQueryFactory.viewerEnd({seriesId: 123, productId: 456, apiPath: '/api/v2/viewer/end'}));
   */
  viewerEnd: (
    variables: {seriesId: number; productId: number; apiPath?: string},
    options?: Omit<
      UseQueryOptions<ViewerEnd, PagewebError>,
      'queryKey' | 'queryFn'
    >
  ) =>
    queryOptions({
      queryKey: VIEWER_QUERY_KEY_MAP.viewerEnd(variables),
      queryFn: async () => {
        const response = await getViewerEnd(variables);
        if (!response.result) {
          throw new PagewebError({
            message: 'Viewer end not found',
            resultCode: response.result_code,
          });
        }
        return {
          ...createViewerEnd(requestContext.requestInfo.user, response.result),
          originalRawDataStr: JSON.stringify(response),
        };
      },
      ...options,
    }),
};
